<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">
      <ReportFilterPopover
        headerTitle="Daily Order Value Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyOrderValueFilter"
        @reset="resetOrderValueFilter">
        <template v-slot:popover>
          <ReportDateRangeSelect 
            :startDate="editingOrderValueFilter.startDate"
            :endDate="editingOrderValueFilter.endDate"
            @updateStartDate="editingOrderValueFilter.startDate = $event"
            @updateEndDate="editingOrderValueFilter.endDate = $event"
          />
          <br />
          Include Services:<br />
          <FormSwitch v-model="editingOrderValueFilter.includeServices" booleanMode />
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedOrderValueFilter.startDate | formatDate}} - {{appliedOrderValueFilter.endDate | formatDate}}
          </div>
          <div>
            <i class="far fa-cogs mr-2"></i>Include Services: {{appliedOrderValueFilter.includeServices ? 'Yes' : 'No'}}
          </div>
        </template>
      </ReportFilterPopover>
      <div class="row no-gutters h-100">
        <div class="col-2">
          <AnalyticsDashboardValueRow>
            <div class="col">
              <AnalyticsDashboardValue title="Total Order Item Value" highlight>
                {{formatCurrency(orderItemSummaryData.orderItemTotalValue)}}
                <template v-slot:info>Ex. Gst. Based on total order items ({{appliedOrderValueFilter.includeServices ? 'including' : 'excluding'}} services) during the period</template>
              </AnalyticsDashboardValue>
            </div>
          </AnalyticsDashboardValueRow>

          <AnalyticsDashboardValueRow>
            <div class="col">
              <AnalyticsDashboardValue title="Average Daily Item Value">
                {{formatCurrency(orderItemSummaryData.averageOrderItemValue)}}
                <template v-slot:info>Ex. Gst. Based on total order items ({{appliedOrderValueFilter.includeServices ? 'including' : 'excluding'}} services) during the period</template>
              </AnalyticsDashboardValue>
            </div>
          </AnalyticsDashboardValueRow>
        </div>
        <div class="col-10">
          <ChartBar
            title="Daily Order Value"
            :series="orderDailyValueData.series"
            :xAxis="orderDailyValueData.xAxis"
            :yAxis="orderDailyValueData.yAxis"
            :legend="orderDailyValueData.legend"
            :options="orderDailyValueData.options"
            :popupEnabled="true"
          />
        </div>
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'OrderItemDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    stockId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderItemSummaryData: {
        orderItemTotalValue: 0,
        averageOrderItemValue: 0,
      },
      orderDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderValueFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        includeServices: false
      },
      editingOrderValueFilter: {},
      appliedOrderValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    stockId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderValueFilter() {
      this.appliedOrderValueFilter = _.clone(this.editingOrderValueFilter)

      this.requestChartData()
    },
    resetOrderValueFilter() {
      this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
      this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var orderItemValueFilter = {
        type: 'daily',
        startDate: this.appliedOrderValueFilter.startDate,
        endDate: this.appliedOrderValueFilter.endDate,
        contactId: this.contactId,
        stockIdList: this.stockId ? [this.stockId] : null,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        includeServices: this.appliedOrderValueFilter.includeServices
      }

      let widgetComp = this

      this.$apiService.analyticsService.getOrderItemsPriceGroupChartData(orderItemValueFilter)
      .then(response => {
        let chartData = response.data

        // Filter out series with no data
        chartData = chartData.filter(seriesInfo => seriesInfo.data.length > 0)

        let fullDateList = _.uniq(chartData.map(seriesInfo => seriesInfo.data.map(dataPoint => moment(dataPoint.date).format('YYYY-MM-DD'))).flat())

        this.orderDailyValueData.legend = {
          data: chartData.map(seriesInfo => seriesInfo.priceGroup.name),
        }
        this.orderDailyValueData.xAxis = {
          type: 'category',
          data: fullDateList
        }
        this.orderDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          }
        ]

        

        this.orderDailyValueData.series = chartData.map(seriesInfo => {

          // Fill in all the dates so we don't have gaps
          let dateMap = {}
          fullDateList.forEach(date => {
            dateMap[date] = 0
          })
          seriesInfo.data.map(dataPoint => {
            dateMap[moment(dataPoint.date).format('YYYY-MM-DD')] = dataPoint.totalLinePrice
          })

          return {
            type: 'bar',
            name: seriesInfo.priceGroup.name,
            stack: 'total',
            data: fullDateList.map(date => {
              return [date, dateMap[date]]
            }),
            tooltip: {
              valueFormatter(value) {
                return widgetComp.formatCurrency(value);
              }
            }
          }
        })

        // Total the data
        this.orderItemSummaryData.orderItemTotalValue = chartData.reduce((curPriceGroupValue, priceGroupInfo) => {
          return curPriceGroupValue + priceGroupInfo.data.reduce((curItemValue, dataPoint) => {
            return curItemValue + dataPoint.totalLinePrice
          }, 0)
        }, 0)
        this.orderItemSummaryData.averageOrderItemValue = 0
        if(fullDateList.length > 0) {
          this.orderItemSummaryData.averageOrderItemValue = this.orderItemSummaryData.orderItemTotalValue / fullDateList.length
        }
      })
    },
  }
}
</script>